'use client';

import { forwardRef } from 'react';
import NextLink from 'next/link';
import { Anchor as MantineAnchor } from '@mantine/core';
import type { AnchorProps as MantineAnchorProps, ElementProps } from '@mantine/core';

import type { LockedProps } from '../types';

export type LinkProps = Omit<MantineAnchorProps, LockedProps> &
  Omit<ElementProps<'a'>, 'className' | 'style' | 'color'> & {
    href: string;
  };

/**
 * ### Link
 *
 * @description
 * Mantine's `Anchor`, always rendered through `next/link` so in-app navigation stays client-side.
 * External hrefs work too — `next/link` hands cross-origin navigation back to the browser — but
 * reach for `ExternalLink` from `@highlight-cp/core-ui-compositions` for those: it adds the
 * `rel`/`target` defaults and the external-link glyph.
 *
 * Locked, and where each concern lives now:
 *
 * - **Look** — `className`/`style` and friends ({@link LockedProps}). Link text sizing and weight
 *   are `size`/`fw`; wrapping is `textWrap`. Spacing and alignment belong on a wrapping `Box` or
 *   `Flex`. The shared link skin (hover color, icon alignment) is the theme's `Anchor` entry.
 * - **Underline** — Mantine's own `underline` prop (`'always' | 'hover' | 'not-hover' | 'never'`,
 *   default `'hover'`), not a boolean.
 * - **`color`** — the deprecated HTML attribute, which silently did nothing. Use `c`.
 *
 * @example
 * ```tsx
 * <Link href="/project/123">Acme Corp</Link>
 * <Link href="/settings" underline="always" size="sm" fw={600}>Settings</Link>
 * ```
 *
 * @param props - {@link LinkProps}
 * @returns React component
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
  <MantineAnchor {...props} component={NextLink} ref={ref} />
));

Link.displayName = 'Link';
